<template>
    <v-dialog :value="value" max-width="760" scrollable @input="(open) => !open && close()">
        <v-card class="ui-card">
            <div class="ui-card__header">
                <div>
                    <h3 class="ui-section-title">Juntar {{ kind === "judge" ? "juízes" : "varas" }} duplicados</h3>
                    <p class="ui-section-description">
                        {{ kind === "judge"
                            ? "Casos, lotações, agendas e andamentos passam para o cadastro que fica. O nome do outro vira grafia: a importação reconhece e não recria."
                            : "Casos, lotações, agendas e alertas passam para a vara que fica. O nome da outra vira grafia: a importação reconhece e não recria." }}
                    </p>
                </div>
            </div>
            <div class="ui-card__body">
                <div class="ui-small ui-muted mb-2">Sugestões (nomes quase iguais{{ kind === "judge" ? " no mesmo tribunal" : " na mesma UF" }}; fica o de mais casos)</div>
                <div v-if="loading" class="pbm-loading"><v-progress-circular indeterminate size="22" width="2" color="primary"></v-progress-circular></div>
                <ui-empty v-else-if="!suggestions.length" text="Nenhum duplicado provável." />
                <div v-for="pair in suggestions" :key="`${pair.source.id}-${pair.target.id}`" class="pbm-pair">
                    <div class="pbm-pair__names">
                        <div><span class="ui-muted">sai</span> {{ label(pair.source) }}</div>
                        <div><span class="ui-muted">fica</span> <strong>{{ label(pair.target) }}</strong></div>
                    </div>
                    <div class="pbm-pair__actions">
                        <v-btn v-if="$allowed('prospect.merge')" small outlined :disabled="merging" title="Inverter: manter o outro" @click="merge(pair.target, pair.source)"><v-icon small>mdi-swap-vertical</v-icon></v-btn>
                        <v-btn v-if="$allowed('prospect.merge')" small color="primary" depressed :loading="merging" @click="merge(pair.source, pair.target)">Juntar</v-btn>
                    </div>
                </div>

                <div class="ui-small ui-muted mt-5 mb-2">Outro par</div>
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-autocomplete v-model="manual.source" :items="options" item-value="id" item-text="text" label="Sai (é excluído)" outlined dense hide-details :menu-props="{ offsetY: true }"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-autocomplete v-model="manual.target" :items="options" item-value="id" item-text="text" label="Fica" outlined dense hide-details :menu-props="{ offsetY: true }"></v-autocomplete>
                    </v-col>
                </v-row>
                <div class="d-flex justify-end mt-3">
                    <v-btn v-if="$allowed('prospect.merge')" small outlined :disabled="!manual.source || !manual.target || manual.source === manual.target || merging" @click="mergeManual">Juntar este par</v-btn>
                </div>
            </div>
            <div class="ui-card__footer">
                <v-btn outlined @click="close">Fechar</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import { notifyError, notifySuccess } from "@/components/prospect/prospect.util";

// Juncao de juizes ou varas duplicados: sugestoes do backend e par escolhido a mao.
export default {
    name: "ProspectBenchMergeDialog",

    props: {
        value: { type: Boolean, default: false },
        kind: { type: String, default: "judge" }, // judge | court
        items: { type: Array, default: () => [] }, // cadastros da lista atual (par manual)
    },

    data: () => ({ suggestions: [], loading: false, merging: false, manual: { source: null, target: null } }),

    computed: {
        base() {
            return this.kind === "judge" ? "/api/admin/prospect/judges" : "/api/admin/prospect/courts";
        },
        options() {
            return this.items.map((item) => ({ id: item.id, text: this.label({ name: item.name, scope: this.kind === "judge" ? item.tribunal : item.uf }) }));
        },
    },

    watch: {
        value(open) {
            if (open) {
                this.manual = { source: null, target: null };
                this.load();
            }
        },
    },

    methods: {
        label(item) {
            return item.scope ? `${item.name} · ${item.scope}` : item.name;
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`${this.base}/merge-suggestions`);
                this.suggestions = result.data.content;
                this.$emit("count", this.suggestions.length);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        mergeManual() {
            const find = (id) => this.items.find((item) => item.id === id);
            const scope = (item) => (this.kind === "judge" ? item.tribunal : item.uf);
            const source = find(this.manual.source);
            const target = find(this.manual.target);
            this.merge({ id: source.id, name: source.name, scope: scope(source) }, { id: target.id, name: target.name, scope: scope(target) });
        },

        async merge(source, target) {
            const what = this.kind === "judge" ? "juiz" : "vara";
            const confirmed = await this.$dialog.confirm({
                title: `Juntar ${what === "juiz" ? "juízes" : "varas"}`,
                text: `"${source.name}" será unido a "${target.name}". Tudo o que estava ligado a ${what === "juiz" ? "ele" : "ela"} passa para o cadastro que fica, e o outro é excluído. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Juntar", color: "primary" } },
            });
            if (!confirmed) return;
            this.merging = true;
            try {
                await axios.post(`${this.base}/merge`, { source_id: source.id, target_id: target.id });
                notifySuccess(this, "Cadastros juntados.");
                this.manual = { source: null, target: null };
                this.$emit("merged");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.merging = false;
            }
        },

        close() {
            this.$emit("input", false);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/ui/tokens";

.pbm-loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
}

.pbm-pair {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid $ui-border-soft;
    font-size: 13px;
}

.pbm-pair__names {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    .ui-muted {
        display: inline-block;
        width: 32px;
    }
}

.pbm-pair__actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}
</style>
